import React from 'react';
import { ChevronLeft, FileText } from 'lucide-react';

interface GenerateReportPageSkeletonProps {
  /**
   * Additional class names
   */
  className?: string;
}

/**
 * GenerateReportPageSkeleton component
 *
 * This component provides a loading skeleton specifically designed for the Generate Report page.
 * It mimics the structure of the template selection step of the report generation process.
 *
 * @example
 * <GenerateReportPageSkeleton />
 */
export const GenerateReportPageSkeleton: React.FC<
  GenerateReportPageSkeletonProps
> = ({ className = '' }) => {
  return (
    <div className={`min-h-screen flex flex-col bg-white ${className}`}>
      <main className="flex-1 pt-4">
        <div className="max-w-[1200px] h-fit mx-auto p-6">
          {/* Header Section Skeleton */}
          <div className="mb-6 sm:mb-8 md:mb-10 animate-pulse">
            <div className="flex items-center gap-2 text-sm sm:text-base text-[#7A7D81] mb-3 sm:mb-4">
              <div className="flex items-center gap-1">
                <ChevronLeft className="w-4 h-4 text-gray-300" />
                <div className="h-4 w-32 bg-gray-200 rounded-md"></div>
              </div>
            </div>
            <div className="h-10 w-64 bg-gray-200 rounded-md"></div>
          </div>

          {/* Main Content Skeleton */}
          <div className="bg-white pb-24">
            <div className="animate-pulse">
              <div className="flex flex-wrap items-center gap-3 mb-4 sm:mb-5 md:mb-6 mt-2 sm:mt-0">
                <div className="h-8 w-64 bg-gray-200 rounded-md"></div>
                <div className="px-2 py-1 h-6 w-20 bg-gray-200 rounded-lg"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                {/* Template Card Skeletons */}
                {[...Array(2)].map((_, index) => (
                  <div
                    key={`template-${index}`}
                    className="w-full p-4 sm:p-5 md:p-6 rounded-xl border border-[#E4E8EA] bg-white"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="flex flex-col gap-4 sm:gap-5 md:gap-6">
                      {/* Preview Area Skeleton */}
                      <div className="relative">
                        <div className="w-full h-[240px] bg-[#F5F8F9] rounded-lg flex items-center justify-center">
                          <FileText className="w-12 h-12 text-gray-300 flex-shrink-0" />
                        </div>
                      </div>

                      {/* Title and Description Skeleton */}
                      <div className="flex flex-col gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="h-6 w-40 bg-gray-200 rounded-md"></div>
                            <div className="p-1.5 h-7 w-7 bg-gray-100 rounded-lg"></div>
                          </div>
                          <div className="h-4 w-full bg-gray-200 rounded-md mt-2"></div>
                          <div className="h-4 w-3/4 bg-gray-200 rounded-md mt-2"></div>
                        </div>

                        {/* Tags Skeleton */}
                        <div className="flex flex-wrap gap-2">
                          {[...Array(4)].map((_, tagIndex) => (
                            <div
                              key={`tag-${index}-${tagIndex}`}
                              className="px-2 py-1 h-6 w-20 bg-gray-100 rounded-lg"
                              style={{
                                animationDelay: `${index * 0.1 + tagIndex * 0.05}s`,
                              }}
                            ></div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Skeleton */}
      <div className="sticky bottom-0 w-full bg-white border-t border-[#E4E8EA] py-4 px-4 sm:px-6 animate-pulse">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center justify-between">
            <div className="h-10 w-24 bg-gray-200 rounded-md"></div>
            <div className="h-10 w-40 bg-gray-200 rounded-md"></div>
          </div>
        </div>
      </div>
    </div>
  );
};
